import React, { useState, useEffect } from 'react';
import DataTable, { Direction } from 'react-data-table-component';
import { SideSlider } from '@wajeez-web/components/ui';
import { useCommonSelector } from '@wajeez-web/stores/common';
import { getLocalTimeFromUTC } from '@wajeez-web/core/utils';
import { Identity } from '@wajeez-web/core/api-base';
import { useTranslation } from 'react-i18next';
import { BiCheck, BiX } from 'react-icons/bi';
import { GetTablecolumnImage } from '@wajeez-web/components/ui';
import { ReturnTranslatedText } from '@wajeez-web/core/localization';

import { tableStyle } from './tableStyle';
import TabeleFilters from './TableFilters';
import ManageTableColumns from './ManageTableColumns';
import TableHeader from './TableHeader';
import TableColumnHeaderComponent from './tableHelperComponents/TableColumnHeaderComponent';
import {
  TableProps,
  ColumnsTypes,
  SelectedColumnsFilters,
  ComparisonValues,
  defaultTablePreferances,
} from './types';
import columnHandler from './tableHelperComponents/ColumnsHandler';

const Table: React.FC<TableProps> = ({
  tableData,
  tableActions,
  setPreferances,
  PreferancesData = defaultTablePreferances,
  isLoading,
  isUpdating,
  defultFilterscomponents,
  columnsSelectors,
  tableHeaderActions,
}) => {
  const [showSideFilters, setShowSideFilters] = useState(false);
  const [selectedColumnsFilters, setSelectedColumnsFilters] = useState<
    SelectedColumnsFilters[]
  >([]);
  const [openManageColumnsModal, setOpenManageColumnsModal] = useState(false);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState<ColumnsTypes[]>([]);

  const dir = useCommonSelector((state) => state.common.settings.dir);

  const { t } = useTranslation();

  const extractData = () => {
    if (tableData) {
      const col: ColumnsTypes[] = tableData['columns'].map((item: any) => ({
        name: (
          <TableColumnHeaderComponent
            header={item.header}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            dataType={Identity.DataType[item.dataType].toString()}
            setSelectedColumnsFilters={handleFilter}
            selectedColumnsFilters={selectedColumnsFilters}
            id={item.value}
            onSort={handleSort}
            PreferancesData={PreferancesData}
            isFilterable={item.isFilterable}
          />
        ),
        selector: (row: any): any => {
          if (
            columnsSelectors &&
            Object.keys(columnsSelectors(row[1], row)).find(
              (key) => key == item.value
            )
          ) {
            return columnsSelectors(row[1], row)[item.value];
          } else {
            switch (item.dataType) {
              case Identity.DataType.DateTime:
                return <>{getLocalTimeFromUTC(row[item.value])}</>;
              case Identity.DataType.Image:
                return (
                  <div
                    key={row[1] + item.value}
                    className="flex justify-center p-2 items-center text-center "
                  >
                    {row[item.value] ? (
                      <GetTablecolumnImage
                        key={row[1] + item.value}
                        id={row[1] + item.value}
                        iconId={row[item.value] || ''}
                      />
                    ) : (
                      <img
                        src={'assets/images/no-imag.jpg'}
                        alt="icon"
                        className="h-14 w-14 m-auto text-center border shadow-sm"
                      />
                    )}
                  </div>
                );
              case Identity.DataType.Boolean:
                if (row[item.value] === 'True')
                  return <BiCheck size={30} color="green" />;
                return <BiX size={30} color="red" />;
              default:
                return <div className="truncate">{row[item.value]}</div>;
            }
          }
        },

        header: item.header,
        minWidth: item.dataType == 3 ? '150px' : '200px',
        maxWidth: item.dataType == 3 ? '150px' : 'auto',
        id: item.value,
        key: item.value,
        isHidden: item.isHidden,
        dataType: Identity.DataType[item.dataType],
        wrap: true,
        isFilterable: item.isFilterable,
      }));

      const data: any = [];

      // eslint-disable-next-line array-callback-return
      // eslint-disable-next-line array-callback-return
      tableData['columns'].map((item: any) => {
        // eslint-disable-next-line array-callback-return
        item.cells.map((element: any, i: number) => {
          if (data[i]) {
            data[i] = { ...data[i], [item.value]: element };
          } else {
            data[i] = { [item.value]: element };
          }
        });
      });

      Promise.all([
        setColumns(col),
        setData(data),
        setSelectedColumnsFilters(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          PreferancesData.filteringOperations.map((item) => {
            const found = col.find((ele) => ele?.id === item?.property);

            const operator = ComparisonValues.find(
              (element) => element.value === item.comparisonType?.toString()
            );

            return {
              label: found?.header || '',
              key: found?.id,
              value: item.cretiria,
              type: found?.dataType,
              operator: operator,
            };
          })
        ),
      ]);
    }
  };

  const handleFilter = (items: SelectedColumnsFilters[]): void => {
    setSelectedColumnsFilters(items);
    setPreferances &&
      setPreferances({
        ...PreferancesData,
        pageNumber: 1,
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        filteringOperations: items
          .filter((ele) => ele.value)
          .map((ele) => {
            return {
              property: parseInt(ele.key),
              cretiria: ele.value || '',
              comparisonType: parseInt(ele.operator?.value),
            };
          }),
      });
  };

  const handleSort = (id: string, direction: Identity.OrderByDirection) => {
    if (id) {
      setPreferances &&
        setPreferances({
          ...PreferancesData,
          pageNumber: 1,
          sortingOperations: [
            {
              property: Number(id),
              orderByDirection: direction,
            },
          ],
        });
    }
  };

  useEffect(() => {
    extractData();
  }, [tableData]);

  return (
    tableData && (
      <div>
        <TableHeader
          tableHeaderActions={tableHeaderActions}
          tableData={tableData}
          defultFilterscomponents={defultFilterscomponents}
          setSelectedColumnsFilters={handleFilter}
          selectedColumnsFilters={selectedColumnsFilters}
          setOpenManageColumnsModal={() => setOpenManageColumnsModal(true)}
          clearFilter={() => {
            handleFilter([]);
          }}
          setShowSideFilters={() => setShowSideFilters(!showSideFilters)}
          columns={columns}
        />
        <div className=" h-full  w-full transition-all ">
          {isUpdating && (
            <div className="linear-activity">
              <div className="indeterminate"></div>
            </div>
          )}

          <DataTable
            columns={columnHandler(columns, tableActions)}
            data={data}
            progressPending={isLoading}
            pagination
            direction={dir === 'rtl' ? Direction.RTL : Direction.LTR}
            paginationServer
            paginationTotalRows={tableData['totalCount']}
            onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
              setPreferances &&
                setPreferances({
                  ...PreferancesData,
                  pageNumber: currentPage,
                  pageSize: currentRowsPerPage,
                });
            }}
            paginationDefaultPage={tableData['currentPage']}
            onChangePage={(page) =>
              setPreferances &&
              setPreferances({ ...PreferancesData, pageNumber: page })
            }
            selectableRows={false}
            paginationComponentOptions={{
              rowsPerPageText: t(`general.${'Rows per page'}`) as string,
              rangeSeparatorText: t(`general.${'of'}`) as string,
            }}
            progressComponent={
              <ReturnTranslatedText text={`general.${'Loading'}`} />
            }
            striped={true}
            persistTableHead={true}
            // selectableRowsComponent={CheckBox}
            customStyles={tableStyle}
          />
          {showSideFilters && (
            <div className=" w-96 overflow-hidden ltr:right-0 rtl:left-0 top-16  fixed bg-white   ">
              <SideSlider
                width="full"
                title="Filters"
                show={showSideFilters}
                close={() => setShowSideFilters(false)}
              >
                <TabeleFilters
                  selectedColumnsFilters={selectedColumnsFilters}
                  setSelectedColumnsFilters={handleFilter}
                  columns={columns}
                  tableData={tableData}
                />
              </SideSlider>
            </div>
          )}
          {openManageColumnsModal && (
            <ManageTableColumns
              open={openManageColumnsModal}
              setOpen={setOpenManageColumnsModal}
              selectedColumns={columns.filter((item) => !item.isHidden)}
              allCols={columns}
              defaultColumns={columns}
              setSelectedColumns={(col) => {
                const flags = col.reduce(
                  (previousValue: number, currentValue: { id: number }) =>
                    previousValue + Number(currentValue.id),
                  0
                );
                setPreferances &&
                  setPreferances({
                    ...PreferancesData,
                    flags: flags,
                  });
                setOpenManageColumnsModal(false);
              }}
            />
          )}
        </div>
      </div>
    )
  );
};

export default Table;
